import api from "@/services/api";
import { Organization } from "@/services/organization";
import {
  PublicDashboardFilters,
  usePublicDashboardQueryFilters,
} from "@/services/public_dashboard";
import { User } from "@/services/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  computed,
  ComputedRef,
  inject,
  MaybeRefOrGetter,
  reactive,
  toValue,
} from "vue";
import { IconName } from "@/services/icons";
import {
  MetricDetail,
  metricDetails,
  MetricKey,
  MetricFormat,
  StaticMetricKey,
} from "@/services/metrics";
import * as lodash from "lodash-es";
import { QueryFunctionContext } from "@/utils/typing";
import type { WidgetConfig } from "@/services/widgets/index";

export enum MetricType {
  MARKETING = "marketing",
  WEBSITE_ANALYTICS = "website_analytics",
  ECOMMERCE = "ecommerce",
  SOCIAL_MEDIA = "social_media",
  SEARCH_TERM_REPORT = "search_term_report",
  CROSS_DISCIPLINE = "cross_discipline",
}

export enum UnitType {
  NUMBER = "number",
  CURRENCY = "currency",
  PERCENT = "percent",
  TIME = "time",
}

export const CALCULATED_METRIC_PREFIX = "calculatedMetric";
export interface CalculatedMetric {
  pk: number;
  name: string;
  description: string;
  // Original tokenized formula as written by the user, e.g. `["(", 1, "+", 2, ")", "*", 3]`.
  formula: (string | number)[];
  createdAt: string;
  modifiedAt: string;
  metricType: MetricType;
  unitType: UnitType;
  user: User["id"];
  organization: Organization["id"];
}

export const metricTypeLabels: Record<MetricType, string> = {
  [MetricType.MARKETING]: "Ad Performance",
  [MetricType.WEBSITE_ANALYTICS]: "Website Analytics",
  [MetricType.ECOMMERCE]: "Ecommerce",
  [MetricType.SOCIAL_MEDIA]: "Social Media",
  [MetricType.SEARCH_TERM_REPORT]: "Search Term Report",
  [MetricType.CROSS_DISCIPLINE]: "Ad Performance + Ecommerce",
};

export const operands = {
  plus: {
    symbol: "+",
    iconName: "plus-operator" as IconName,
  },
  minus: {
    symbol: "-",
    iconName: "minus-operator" as IconName,
  },
  multiply: {
    symbol: "*",
    iconName: "multiply-operator" as IconName,
  },
  divide: {
    symbol: "/",
    iconName: "divide-operator" as IconName,
  },
  exponent: {
    symbol: "^",
    iconName: "exponent-operator" as IconName,
  },
  "parenthesis-left": {
    symbol: "(",
    iconName: "parenthesis-left" as IconName,
  },
  "parenthesis-right": {
    symbol: ")",
    iconName: "parenthesis-right" as IconName,
  },
};

export const LIST_CALCULATED_METRICS_URL =
  "/resources/calculated_metrics/calculated_metrics/";

function listCalculatedMetrics({
  queryKey: [, params],
}: QueryFunctionContext<PublicDashboardFilters>): Promise<CalculatedMetric[]> {
  return api
    .get(LIST_CALCULATED_METRICS_URL, { params })
    .then((response) => response.data);
}

export function useCalculatedMetricsQuery() {
  return reactive(
    useQuery({
      queryKey: [
        "calculatedMetrics",
        { ...usePublicDashboardQueryFilters() },
      ] as const,
      queryFn: listCalculatedMetrics,
    }),
  );
}

export type CreateCalculatedMetricPayload = Pick<
  CalculatedMetric,
  "name" | "description" | "formula" | "metricType" | "unitType"
>;

export function createCalculatedMetric(
  calculatedMetric: CreateCalculatedMetricPayload,
): Promise<CalculatedMetric> {
  return api
    .post("/resources/calculated_metrics/calculated_metrics/", calculatedMetric)
    .then((response) => response.data);
}

export function useCreateCalculatedMetricMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: createCalculatedMetric,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["calculatedMetrics"],
        });
      },
    }),
  );
}

export type UpdateCalculatedMetricPayload = Pick<
  CalculatedMetric,
  "pk" | "name" | "description" | "formula" | "metricType" | "unitType"
>;

export function updateCalculatedMetric(
  calculatedMetric: UpdateCalculatedMetricPayload,
): Promise<CalculatedMetric> {
  return api
    .patch(
      `/resources/calculated_metrics/calculated_metrics/${calculatedMetric.pk}/`,
      calculatedMetric,
    )
    .then((response) => response.data);
}

export function useUpdateCalculatedMetricMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateCalculatedMetric,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["calculatedMetrics"] });
      },
    }),
  );
}

export function deleteCalculatedMetric(
  pk: CalculatedMetric["pk"],
): Promise<void> {
  return api.delete(`/resources/calculated_metrics/calculated_metrics/${pk}/`);
}

export function useDeleteCalculatedMetricMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: deleteCalculatedMetric,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["calculatedMetrics"] });
        queryClient.invalidateQueries({
          queryKey: ["profile"],
        });
      },
    }),
  );
}

export function cleanToken(token: string | number): string {
  if (typeof token === "string") {
    const cleanedToken = token.replace(/[{}]/g, "");
    const camelCasedToken = lodash.camelCase(cleanedToken);
    if (metricDetails[camelCasedToken as MetricKey]) {
      return metricDetails[camelCasedToken as MetricKey].shortName;
    }

    return token;
  }
  return token.toString();
}

const unitTypeToMetricFormat: Record<UnitType, MetricFormat> = {
  [UnitType.NUMBER]: MetricFormat.DECIMAL,
  [UnitType.CURRENCY]: MetricFormat.MONETARY,
  [UnitType.PERCENT]: MetricFormat.RELATIVE,
  [UnitType.TIME]: MetricFormat.DURATION,
};

export type CalculatedMetricKey = `${typeof CALCULATED_METRIC_PREFIX}${number}`;

export function calculatedMetricKey(
  calculatedMetric: CalculatedMetric,
): CalculatedMetricKey {
  return `${CALCULATED_METRIC_PREFIX}${calculatedMetric.pk}`;
}

export function isCalculatedMetric(
  metric: string,
): metric is CalculatedMetricKey {
  return metric.startsWith(CALCULATED_METRIC_PREFIX);
}

// True when any selected metric is a calculated metric of the given type.
// Used to route widgets to the correct endpoint — notably so a cross-discipline
// metric (marketing + ecommerce) isn't forced onto a single-discipline hook.
export function selectedMetricsIncludeCalculatedType(
  selectedMetrics: readonly string[],
  calculatedMetrics: readonly CalculatedMetric[],
  metricType: MetricType,
): boolean {
  const keys = new Set(
    calculatedMetrics
      .filter((cm) => cm.metricType === metricType)
      .map((cm) => calculatedMetricKey(cm)),
  );
  return selectedMetrics.some(
    (metric) => isCalculatedMetric(metric) && keys.has(metric),
  );
}

export type DynamicMetricDetails = Record<StaticMetricKey, MetricDetail> &
  Partial<Record<CalculatedMetricKey, MetricDetail>>;

export function useDynamicMetricDetails() {
  const injectedMetrics = inject<ComputedRef<WidgetConfig | undefined>>(
    "widgetConfig",
    computed(() => undefined),
  );

  const calculatedMetrics = useCalculatedMetricsQuery();

  return computed(() => {
    const dynamicMetricDetails: DynamicMetricDetails = {
      ...metricDetails,
    };

    Object.entries(metricDetails).forEach(([key, detail]) => {
      dynamicMetricDetails[key as StaticMetricKey] = {
        ...detail,
        key: key as StaticMetricKey,
      };
    });

    calculatedMetrics.data?.forEach((calculatedMetric) => {
      const key = calculatedMetricKey(calculatedMetric);
      dynamicMetricDetails[key] = {
        shortName: calculatedMetric.name,
        longName: calculatedMetric.name,
        description: calculatedMetric.description,
        formatType:
          unitTypeToMetricFormat[calculatedMetric.unitType as UnitType],
        summable: false,
        aggregatable: false,
        invertedPositive: false,
        invertedDirection: false,
        key,
      };
    });

    const storedCalculatedMetrics =
      injectedMetrics?.value?.calculatedMetricInfo;
    if (storedCalculatedMetrics) {
      storedCalculatedMetrics.forEach((metric) => {
        dynamicMetricDetails[metric.key] = metric;
      });
    }

    return dynamicMetricDetails;
  });
}

export function getCalculatedMetricVariables(
  calculatedMetric: CalculatedMetric,
) {
  return new Set(
    calculatedMetric.formula
      .filter(
        (element): element is string =>
          typeof element === "string" && element.startsWith("{"),
      )
      .map(
        (element) =>
          lodash.camelCase(
            element.replace("{", "").replace("}", ""),
          ) as MetricKey,
      ),
  );
}

export function useCalculatedMetricKeys(
  metricType: MaybeRefOrGetter<MetricType>,
  validMetrics?: MaybeRefOrGetter<readonly MetricKey[]>,
) {
  const calculatedMetrics = useCalculatedMetricsQuery();
  return computed(() => {
    const validMetricsSet = validMetrics
      ? new Set(toValue(validMetrics))
      : undefined;
    return (
      calculatedMetrics.data
        ?.filter((calculatedMetric) => {
          if (calculatedMetric.metricType !== toValue(metricType)) {
            return false;
          }
          if (validMetricsSet) {
            const variables = getCalculatedMetricVariables(calculatedMetric);
            return [...variables].every((variable) =>
              validMetricsSet.has(variable),
            );
          }
          return true;
        })
        .map(calculatedMetricKey) ?? []
    );
  });
}

// Cache for compiled formula functions (prevents re-parsing formulas)
const compiledFormulaCache = new Map<
  string,
  (data: Record<string, number | null>) => number | null
>();

/**
 * Compiles a formula array into an executable function.
 * Uses a cache to prevent re-compilation of the same formula.
 *
 * @param formula - Array of tokens (strings and numbers) representing the formula
 * @returns Compiled function that takes data and returns the evaluated result, or null if compilation fails
 */
export function compileFormula(
  formula: (string | number)[],
): ((data: Record<string, number | null>) => number | null) | null {
  const cacheKey = JSON.stringify(formula);

  // Return cached compiled function if available
  if (compiledFormulaCache.has(cacheKey)) {
    return compiledFormulaCache.get(cacheKey)!;
  }

  try {
    // Build the expression template with variable names
    let expression = "";
    const variables: string[] = [];

    for (const token of formula) {
      if (typeof token === "number") {
        expression += token.toString();
      } else if (typeof token === "string") {
        if (token.startsWith("{") && token.endsWith("}")) {
          const variableName = lodash.camelCase(
            token.replace("{", "").replace("}", ""),
          );
          variables.push(variableName);
          expression += variableName;
        } else {
          expression += token;
        }
      }
    }

    // Create a compiled function that takes data and evaluates
    function compiledFn(data: Record<string, number | null>): number | null {
      // Check if all required variables exist
      for (const variable of variables) {
        if (data[variable] === null || data[variable] === undefined) {
          return null;
        }
      }

      try {
        // Build parameter list and values
        const params = variables.join(", ");
        const values = variables.map((v) => data[v]);

        // Compile and execute function
        const fn = Function(
          params,
          '"use strict"; return (' + expression + ")",
        ) as (...args: number[]) => number;
        const result = fn(...(values as number[]));

        if (typeof result === "number" && !isNaN(result) && isFinite(result)) {
          return result;
        }
        return null;
      } catch {
        return null;
      }
    }

    // Cache the compiled function
    compiledFormulaCache.set(cacheKey, compiledFn);
    return compiledFn;
  } catch {
    return null;
  }
}

/**
 * Evaluates a formula with the provided data.
 *
 * @param formula - Array of tokens (strings and numbers) representing the formula
 * @param data - Object containing metric values keyed by metric name
 * @returns The evaluated result or null if evaluation fails
 */
export function evaluateFormula(
  formula: (string | number)[],
  data: Record<string, number | null>,
): number | null {
  const compiledFn = compileFormula(formula);
  return compiledFn ? compiledFn(data) : null;
}
